/**
 * Adds LTMS-style collapse toggles to Bootstrap cards inside a root element.
 * Skips cards that already have custom collapse (stages, roles, dropdown tables).
 */
import { i18n } from '@/i18n'
import { syncFloatingSelects } from '@/composables/useCustomSelects'


function hasCustomCollapse(header) {
  if (!header) return true
  if (header.tagName === 'A') return true
  if (header.querySelector('.stage-toggle-icon')) return true
  if (header.querySelector('a .bi-plus-circle, a .bi-dash-circle')) return true
  return false
}

function toggleCard(card) {
  const btn = card.querySelector(':scope > .card-header > .card-collapse-btn')
  const collapsed = card.classList.toggle('is-collapsed')
  if (btn) {
    btn.setAttribute('aria-expanded', String(!collapsed))
    btn.setAttribute('aria-label', i18n.global.t(collapsed ? 'common.expand' : 'common.collapse'))
  }
  // Тело уже display:none. Оверлеи селектов на body прячем до первой отрисовки,
  // иначе они секунду лежат поверх полей, которые поднялись на их место.
  syncFloatingSelects()
}

function ensureCollapseButton(card) {
  const header = card.querySelector(':scope > .card-header')
  const body = card.querySelector(':scope > .card-body')
  if (!header || !body) return
  if (card.matches('[data-no-collapse], .expenses')) return
  if (hasCustomCollapse(header)) return

  card.classList.add('card-collapsible')
  header.classList.add('card-collapsible__header')

  if (header.querySelector(':scope > .card-collapse-btn')) {
    card.dataset.collapseReady = '1'
    return
  }

  // <a>, not <button>: Form wraps slots in fieldset[disabled] when not editing
  const btn = document.createElement('a')
  btn.href = '#'
  btn.role = 'button'
  btn.className = 'card-collapse-btn'
  btn.setAttribute('aria-expanded', 'true')
  btn.setAttribute('aria-label', i18n.global.t('common.collapse'))
  btn.innerHTML =
    '<i class="bi bi-chevron-down collapse-icon" aria-hidden="true"></i>'

  header.appendChild(btn)
  card.dataset.collapseReady = '1'
}

export function enhanceCardCollapse(root) {
  if (!root?.querySelectorAll) return

  root.querySelectorAll('.card').forEach(card => {
    const header = card.querySelector(':scope > .card-header')
    const existingBtn = header?.querySelector(':scope > .card-collapse-btn')
    if (card.dataset.collapseReady === '1' && existingBtn) return
    ensureCollapseButton(card)
  })
}

/**
 * Keep collapse buttons attached after Vue re-renders card headers.
 */
export function useCardCollapse(rootRef) {
  let observer = null
  let scheduled = false

  const run = () => {
    const root = rootRef?.value
    if (root) enhanceCardCollapse(root)
  }

  const schedule = () => {
    if (scheduled) return
    scheduled = true
    queueMicrotask(() => {
      scheduled = false
      run()
    })
  }

  const onClick = event => {
    const root = rootRef?.value
    if (!root?.contains(event.target)) return

    const btn = event.target.closest('.card-collapse-btn')
    if (btn && root.contains(btn)) {
      const card = btn.closest('.card.card-collapsible')
      if (!card) return
      event.preventDefault()
      event.stopPropagation()
      toggleCard(card)
      return
    }

    const header = event.target.closest('.card-collapsible__header')
    if (!header || !root.contains(header)) return
    if (
      event.target.closest(
        'a:not(.card-collapse-btn), input, select, textarea, button, .btn, .dropdown, label, .form-check'
      )
    ) {
      return
    }
    const card = header.closest('.card.card-collapsible')
    if (!card) return
    event.preventDefault()
    toggleCard(card)
  }

  const start = () => {
    run()
    document.addEventListener('click', onClick, true)

    const root = rootRef?.value
    if (!root || typeof MutationObserver === 'undefined') return

    observer?.disconnect()
    observer = new MutationObserver(schedule)
    observer.observe(root, { childList: true, subtree: true })
  }

  const stop = () => {
    document.removeEventListener('click', onClick, true)
    observer?.disconnect()
    observer = null
  }

  return { start, stop, run }
}
